/**
 * XState Actor List Component
 * 
 * Displays all registered actors with pin/select functionality.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export interface ActorInfo {
  id: string;
  assetId?: string;
  type: 'character' | 'projectile' | 'object';
  stateLabel: string;
  entityType?: string;
  behaviorId?: string;
  // Extended info for detailed display
  position?: { x: number; y: number; z: number };
  health?: { current: number; max: number };
  velocity?: { x: number; y: number; z: number };
  isOnScreen?: boolean;
  recentStates?: string[]; // Last 3-5 states
  
  // Character-specific
  locomotionState?: string;
  combatState?: string;
  healthState?: string;
  facingDirection?: number; // +1 or -1
  isGrounded?: boolean;
  
  // Attack-specific
  attackPhase?: string;
  attackId?: string;
}

@customElement('xstate-actor-list')
export class XStateActorList extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .actor-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .actor-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 6px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.15s;
      font-size: 11px;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }
    
    .actor-main-row {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      min-width: 0;
      flex: 1;
    }
    
    .actor-right-section {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .actor-row:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .actor-row.selected {
      background: rgba(100, 180, 255, 0.08);
    }

    .actor-row.pinned {
      background: rgba(255, 200, 100, 0.05);
    }

    .actor-row.selected.pinned {
      background: rgba(100, 180, 255, 0.12);
      border-left: 2px solid #fc9;
    }

    .select-indicator {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.4);
      flex-shrink: 0;
    }

    .select-indicator.selected {
      background: #6bf;
      border-color: #6bf;
    }

    .actor-id {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #ddd;
      flex-shrink: 1;
      min-width: 0;
    }

    .actor-id.character {
      color: #9cf;
    }

    .actor-id.projectile {
      color: #fc9;
    }

    .actor-id.object {
      color: #9f9;
    }

    .state-label {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.1);
      color: #aaa;
      font-weight: 500;
      flex-shrink: 0;
    }

    .type-pill {
      font-size: 9px;
      padding: 1px 4px;
      border-radius: 3px;
      text-transform: uppercase;
      opacity: 0.7;
      margin-right: 4px;
      font-weight: 600;
    }
    
    .type-pill.character { background: rgba(100, 180, 255, 0.2); color: #9cf; }
    .type-pill.projectile { background: rgba(255, 200, 100, 0.2); color: #fc9; }
    .type-pill.object { background: rgba(100, 255, 100, 0.2); color: #9f9; }
    
    .entity-type-pill {
      font-size: 9px;
      padding: 1px 4px;
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.1);
      color: #ddd;
      margin-right: 4px;
    }

    .behavior-id {
      font-size: 9px;
      color: #b8f;
      margin-right: 4px;
      font-family: monospace;
    }

    .asset-id {
      font-size: 9px;
      color: #888;
      margin-right: 4px;
      font-family: monospace;
      opacity: 0.8;
    }

    .actor-details {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 10px;
      opacity: 0.9;
      flex-shrink: 0;
    }
    
    .detail-item {
      display: flex;
      align-items: center;
      gap: 3px;
      white-space: nowrap;
    }
    
    .detail-label {
      opacity: 0.6;
    }
    
    .detail-value {
      font-family: 'Courier New', monospace;
      color: #4fc3f7;
    }
    
    .hp-bar-mini {
      width: 50px;
      height: 4px;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 2px;
      overflow: hidden;
    }
    
    .hp-fill {
      height: 100%;
      background: linear-gradient(90deg, #4caf50, #66bb6a);
      transition: width 0.3s ease;
    }
    
    .hp-fill.damaged {
      background: linear-gradient(90deg, #ff9800, #ffa726);
    }
    
    .hp-fill.critical {
      background: linear-gradient(90deg, #f44336, #e57373);
    }
    
    .state-history {
      display: flex;
      gap: 3px;
      align-items: center;
      font-size: 9px;
      padding-left: 18px;
      opacity: 0.6;
    }
    
    .state-history-item {
      padding: 1px 4px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
    }
    
    .state-history-arrow {
      opacity: 0.4;
    }

    .pin-btn {
      padding: 2px 4px;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 12px;
      opacity: 0.4;
      transition: opacity 0.15s;
    }

    .pin-btn:hover {
      opacity: 0.8;
    }

    .pin-btn.pinned {
      opacity: 1;
      color: #fc9;
    }

    .empty-state {
      text-align: center;
      padding: 16px;
      color: #888;
      font-style: italic;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 8px;
      margin-bottom: 4px;
      font-size: 10px;
      text-transform: uppercase;
      color: #888;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .count-badge {
      background: rgba(255, 255, 255, 0.1);
      padding: 2px 6px;
      border-radius: 3px;
    }
  `;

  @property({ type: Array }) actors: ActorInfo[] = [];
  @property({ type: String }) selectedId: string | null = null;
  @property({ type: Array }) pinnedIds: string[] = [];

  render() {
    if (this.actors.length === 0) {
      return html`<div class="empty-state">No actors registered</div>`;
    }

    return html`
      <div class="header">
        <span>Actors</span>
        <span class="count-badge">${this.actors.length}</span>
      </div>
      <div class="actor-list">
        ${this.actors.map(actor => this.renderActorRow(actor))}
      </div>
    `;
  }

  private renderActorRow(actor: ActorInfo) {
    const isSelected = this.selectedId === actor.id;
    const isPinned = this.pinnedIds.includes(actor.id);

    return html`
      <div 
        class="actor-row ${isSelected ? 'selected' : ''} ${isPinned ? 'pinned' : ''}"
        @click=${() => this.selectActor(actor.id)}
      >
        <div class="actor-main-row">
          <div class="select-indicator ${isSelected ? 'selected' : ''}"></div>
          <span class="actor-id ${actor.type}">${actor.id}</span>
          ${actor.entityType 
            ? html`<span class="entity-type-pill">${actor.entityType}</span>` 
            : html`<span class="type-pill ${actor.type}">${actor.type}</span>`
          }
          ${actor.behaviorId ? html`<span class="behavior-id">B:${actor.behaviorId}</span>` : ''}
          ${actor.assetId && actor.assetId !== actor.behaviorId ? html`<span class="asset-id">(${actor.assetId})</span>` : ''}
          <span class="state-label">${actor.stateLabel}</span>
        </div>
        
        <div class="actor-right-section">
          ${this.renderActorDetails(actor)}
          
          <button 
            class="pin-btn ${isPinned ? 'pinned' : ''}"
            @click=${(e: Event) => { e.stopPropagation(); this.togglePin(actor.id); }}
            title="${isPinned ? 'Unpin' : 'Pin'}"
          >
            📌
          </button>
        </div>
      </div>
    `;
  }
  
  private renderActorDetails(actor: ActorInfo) {
    // Render type-specific details
    if (actor.type === 'character') {
      return this.renderCharacterDetails(actor);
    } else if (actor.type === 'projectile') {
      return this.renderProjectileDetails(actor);
    } else if (actor.type === 'object') {
      return this.renderObjectDetails(actor);
    }
    return '';
  }
  
  private renderCharacterDetails(actor: ActorInfo) {
    const details = [];
    
    // Health with visual bar
    if (actor.health) {
      const hpPercent = actor.health.max > 0 ? (actor.health.current / actor.health.max) * 100 : 0;
      const hpClass = hpPercent > 60 ? '' : hpPercent > 30 ? 'damaged' : 'critical';
      details.push(html`
        <div class="detail-item">
          <span class="detail-label">HP:</span>
          <div class="hp-bar-mini">
            <div class="hp-fill ${hpClass}" style="width: ${hpPercent}%"></div>
          </div>
          <span class="detail-value">${actor.health.current}/${actor.health.max}</span>
        </div>
      `);
    }
    
    // Locomotion state
    if (actor.locomotionState) {
      details.push(html`<span class="detail-value">${actor.locomotionState}</span>`);
    }
    
    // Combat state (orange if not idle)
    if (actor.combatState && actor.combatState !== 'idle') {
      details.push(html`<span class="detail-value" style="color: #fc9">${actor.combatState}</span>`);
    }
    
    // Attack phase (red with attack ID)
    if (actor.attackPhase) {
      details.push(html`<span class="detail-value" style="color: #f66">${actor.attackId || 'atk'}:${actor.attackPhase}</span>`);
    }
    
    // Grounded status
    if (actor.isGrounded !== undefined) {
      details.push(html`<span class="detail-value">${actor.isGrounded ? '⬇️' : '⬆️'}</span>`);
    }
    
    // Facing direction
    if (actor.facingDirection !== undefined) {
      details.push(html`<span class="detail-value">${actor.facingDirection > 0 ? '→' : '←'}</span>`);
    }
    
    return details.length > 0 ? html`<div class="actor-details">${details}</div>` : '';
  }
  
  private renderProjectileDetails(actor: ActorInfo) {
    const details = [];
    
    // Position
    if (actor.position) {
      details.push(html`
        <div class="detail-item">
          <span class="detail-label">Pos:</span>
          <span class="detail-value">${actor.position.x.toFixed(1)}, ${actor.position.y.toFixed(1)}, ${actor.position.z.toFixed(1)}</span>
        </div>
      `);
    }
    
    // Velocity/speed
    if (actor.velocity) {
      const speed = Math.sqrt(actor.velocity.x ** 2 + actor.velocity.y ** 2 + actor.velocity.z ** 2);
      details.push(html`
        <div class="detail-item">
          <span class="detail-label">Speed:</span>
          <span class="detail-value">${speed.toFixed(1)} m/s</span>
        </div>
      `);
    }
    
    return details.length > 0 ? html`<div class="actor-details">${details}</div>` : '';
  }
  
  private renderObjectDetails(actor: ActorInfo) {
    const details = [];
    
    // Health with visual bar
    if (actor.health) {
      const hpPercent = actor.health.max > 0 ? (actor.health.current / actor.health.max) * 100 : 0;
      const hpClass = hpPercent > 60 ? '' : hpPercent > 30 ? 'damaged' : 'critical';
      details.push(html`
        <div class="detail-item">
          <span class="detail-label">HP:</span>
          <div class="hp-bar-mini">
            <div class="hp-fill ${hpClass}" style="width: ${hpPercent}%"></div>
          </div>
          <span class="detail-value">${actor.health.current}/${actor.health.max}</span>
        </div>
      `);
    }
    
    return details.length > 0 ? html`<div class="actor-details">${details}</div>` : '';
  }
  


  private selectActor(actorId: string) {
    this.dispatchEvent(new CustomEvent('actor-selected', {
      detail: { actorId },
      bubbles: true,
      composed: true,
    }));
  }

  private togglePin(actorId: string) {
    const isPinned = this.pinnedIds.includes(actorId);
    this.dispatchEvent(new CustomEvent('actor-pinned', {
      detail: { actorId, pinned: !isPinned },
      bubbles: true,
      composed: true,
    }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'xstate-actor-list': XStateActorList;
  }
}
